// web-hls-video.ts
import Hls from "hls.js";

class WebHlsVideo extends HTMLElement {
  videoRef: HTMLVideoElement | null = null;
  playBtnRef: HTMLButtonElement | null = null;
  observer: IntersectionObserver;

  constructor() {
    super();
    this.videoRef = null;
    this.playBtnRef = null;
    this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
      threshold: 0.1,
    });
  }

  connectedCallback() {
    this.videoRef = this.querySelector("video");
    this.playBtnRef = this.querySelector("button");

    const loading = this.getAttribute("loading") ?? "lazy";
    if (loading === "eager") {
      this.initHlsPlayer();
    } else {
      this.observer.observe(this);
      this.playBtnRef?.addEventListener("click", this.onPlayBtnClick.bind(this));
    }
  }

  disconnectedCallback() {
    this.observer.disconnect();
    this.videoRef?.removeEventListener("pause", this.onPause);
    this.videoRef?.removeEventListener("play", this.onPlay);
    this.videoRef?.removeEventListener("ended", this.onEnded);
    this.videoRef?.removeEventListener("loadedmetadata", this.onCanPlay);
    this.playBtnRef?.removeEventListener("click", this.onPlayBtnClick);
  }

  onIntersection(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.observer.unobserve(this);
        this.classList.add("is-visible");
        this.initHlsPlayer();
      }
    });
  }

  initHlsPlayer() {
    if (!this.videoRef) return;

    const videoSrc = this.getAttribute("hls-playlist-url");
    if (!videoSrc) return;

    this.videoRef.addEventListener("pause", this.onPause.bind(this));
    this.videoRef.addEventListener("play", this.onPlay.bind(this));
    this.videoRef.addEventListener("ended", this.onEnded.bind(this));

    const onCanPlay = this.onCanPlay.bind(this);

    if (Hls.isSupported()) {
      const hls = new Hls({ debug: false, startLevel: 10 });
      hls.loadSource(videoSrc);
      hls.attachMedia(this.videoRef);
      hls.subtitleDisplay = true;
      hls.once(Hls.Events.BUFFER_APPENDED, onCanPlay);
    } else if (this.videoRef.canPlayType("application/vnd.apple.mpegurl")) {
      this.videoRef.src = videoSrc;
      this.videoRef.addEventListener("loadedmetadata", onCanPlay);
    }
  }

  onPause = () => this.classList.remove("is-playing");
  onPlay = () => {
    this.classList.add("is-playing");
    setTimeout(() => this.classList.add("hide-poster"), 250);
  };
  onEnded = () => this.classList.remove("hide-poster");
  onCanPlay = () => {
    this.classList.add("is-loaded");
    setTimeout(() => this.classList.add("controls-initiated"), 500);
  };

  onPlayBtnClick = () => {
    const isPlaying = this.classList.contains("is-playing");
    if (isPlaying) {
      this.videoRef?.pause();
    } else {
      this.videoRef?.play();
    }
  };
}

customElements.define("web-hls-video", WebHlsVideo);
